
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertTriangle, ChevronDown, ChevronRight, FileText, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

const fmt = (v) => `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Generate month options for the last 12 months
function getMonthOptions() {
  const opts = [{ value: 'all', label: 'All Months' }];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = d.toISOString().slice(0, 7);
    const label = d.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
    opts.push({ value: val, label });
  }
  return opts;
}

export function ProjectHoursTab({ onCreateInvoice }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState('all');
  const [expanded, setExpanded] = useState(new Set());
  const monthOptions = getMonthOptions();

  const load = useCallback(() => {
    setLoading(true);
    const params = month !== 'all' ? { month } : {};
    api.get('/invoices/project-hours-summary', { params }).then(r => {
      setData(r.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const toggleExpand = (pid) => {
    setExpanded(prev => {
      const s = new Set(prev);
      s.has(pid) ? s.delete(pid) : s.add(pid);
      return s;
    });
  };

  const uninvoicedCount = data.filter(d => d.has_uninvoiced_hours).length;

  if (loading) return <div className="animate-pulse text-muted-foreground p-4">Loading project hours...</div>;

  return (
    <div className="space-y-4" data-testid="project-hours-tab">
      {/* Filter */}
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-base" data-testid="project-hours-heading">Project Hours & Billing</h2>
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="w-48 h-9 bg-card border-border text-xs" data-testid="project-hours-month-filter">
            <SelectValue placeholder="All Months" />
          </SelectTrigger>
          <SelectContent className="bg-card border-border max-h-60">
            {monthOptions.map(o => <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Uninvoiced tip */}
      {uninvoicedCount > 0 && (
        <div className="flex items-center gap-2.5 p-3 rounded-md bg-amber-500/10 border border-amber-500/20" data-testid="uninvoiced-hours-tip">
          <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0" />
          <p className="text-xs text-amber-400">
            <span className="font-semibold">{uninvoicedCount} project{uninvoicedCount !== 1 ? 's have' : ' has'} logged hours but no invoices created yet.</span>
            {' '}Review the highlighted rows below to ensure billing is up to date.
          </p>
        </div>
      )}

      {data.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">No projects with hours or invoices for this period.</p>
      ) : (
        <div className="border border-border rounded-md bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-xs uppercase tracking-wider w-8"></TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Project</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Client</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">Hours</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">Billable</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-center">Invoices</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">Billed</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">Outstanding</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-center">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map(p => {
                const isExpanded = expanded.has(p.project_id);
                const hasSections = (p.sections?.length > 0) || (p.section_billing?.length > 0);
                // Merge section hours and billing
                const sectionMap = {};
                (p.sections || []).forEach(s => {
                  sectionMap[s.section_id] = { ...s, billed: 0, section_invoices: 0 };
                });
                (p.section_billing || []).forEach(sb => {
                  if (sectionMap[sb.section_id]) {
                    sectionMap[sb.section_id].billed = sb.total_billed;
                    sectionMap[sb.section_id].section_invoices = sb.invoice_count;
                  } else {
                    sectionMap[sb.section_id] = {
                      section_id: sb.section_id,
                      section_name: sb.section_name,
                      section_number: sb.section_number,
                      hours: 0, billable_hours: 0,
                      billed: sb.total_billed,
                      section_invoices: sb.invoice_count,
                    };
                  }
                });
                const mergedSections = Object.values(sectionMap);

                return (
                  <React.Fragment key={p.project_id}>
                    <TableRow
                      className={`border-border cursor-pointer transition-colors ${p.has_uninvoiced_hours ? 'bg-amber-500/5 hover:bg-amber-500/10' : 'hover:bg-secondary/20'}`}
                      onClick={() => hasSections && toggleExpand(p.project_id)}
                      data-testid={`project-row-${p.project_id}`}
                    >
                      <TableCell className="w-8 text-center">
                        {hasSections ? (
                          isExpanded ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground inline" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground inline" />
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <p className="text-xs font-medium">{p.project_number}</p>
                        <p className="text-xs text-muted-foreground truncate max-w-[180px]">{p.project_name}</p>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{p.client_name || '-'}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums font-medium">{p.total_hours}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums text-emerald-400">{p.billable_hours}</TableCell>
                      <TableCell className="text-xs text-center">
                        {p.invoice_count > 0 ? (
                          <Badge variant="outline" className="text-xs">
                            <FileText className="h-2.5 w-2.5 mr-0.5" />{p.invoice_count}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{fmt(p.total_billed)}</TableCell>
                      <TableCell className={`text-xs text-right tabular-nums ${p.outstanding > 0 ? 'text-amber-400 font-medium' : ''}`}>{fmt(p.outstanding)}</TableCell>
                      <TableCell className="text-center">
                        {p.has_uninvoiced_hours ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 text-xs gap-1 border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                            onClick={(e) => { e.stopPropagation(); onCreateInvoice?.(p.project_id, month !== 'all' ? month : undefined); }}
                            data-testid={`create-invoice-${p.project_id}`}
                          >
                            <Plus className="h-3 w-3" /> Create Invoice
                          </Button>
                        ) : p.outstanding > 0 ? (
                          <Badge className="text-xs bg-blue-500/15 text-blue-400 border-blue-500/25">Outstanding</Badge>
                        ) : p.total_billed > 0 ? (
                          <Badge className="text-xs bg-emerald-500/15 text-emerald-400 border-emerald-500/25">Billed</Badge>
                        ) : null}
                      </TableCell>
                    </TableRow>

                    {/* Expanded section details */}
                    {isExpanded && mergedSections.length > 0 && mergedSections.map(s => (
                      <TableRow key={s.section_id} className="border-border/50 bg-secondary/10 hover:bg-secondary/20">
                        <TableCell></TableCell>
                        <TableCell colSpan={2} className="text-xs text-muted-foreground pl-6">
                          {s.section_number ? `${s.section_number} · ` : ''}{s.section_name}
                        </TableCell>
                        <TableCell className="text-xs text-right tabular-nums">{(s.hours || 0).toFixed(1)}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums text-emerald-400/80">{(s.billable_hours || 0).toFixed(1)}</TableCell>
                        <TableCell className="text-xs text-center tabular-nums text-muted-foreground">{s.section_invoices || '-'}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums">{fmt(s.billed)}</TableCell>
                        <TableCell colSpan={2}></TableCell>
                      </TableRow>
                    ))}
                    {isExpanded && mergedSections.length === 0 && (
                      <TableRow className="border-border/50 bg-secondary/10">
                        <TableCell></TableCell>
                        <TableCell colSpan={8} className="text-xs text-muted-foreground pl-6 italic">No section breakdown available</TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Summary Cards */}
      {data.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="project-hours-summary-cards">
          <Card className="border-border bg-card shadow-none">
            <CardContent className="p-3 text-center">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Total Hours</p>
              <p className="text-lg font-bold mt-0.5 tabular-nums">{data.reduce((s, d) => s + d.total_hours, 0).toFixed(1)}</p>
            </CardContent>
          </Card>
          <Card className="border-border bg-card shadow-none">
            <CardContent className="p-3 text-center">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Total Billed</p>
              <p className="text-lg font-bold mt-0.5 tabular-nums text-emerald-400">{fmt(data.reduce((s, d) => s + d.total_billed, 0))}</p>
            </CardContent>
          </Card>
          <Card className="border-border bg-card shadow-none">
            <CardContent className="p-3 text-center">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Outstanding</p>
              <p className="text-lg font-bold mt-0.5 tabular-nums text-amber-400">{fmt(data.reduce((s, d) => s + d.outstanding, 0))}</p>
            </CardContent>
          </Card>
          <Card className="border-border bg-card shadow-none">
            <CardContent className="p-3 text-center">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Projects</p>
              <p className="text-lg font-bold mt-0.5 tabular-nums">{data.length}</p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
